import { useEffect, useState } from "react";
import { Alert, Box, Button, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { Absence, PlaybookGroup, PositionStatus, Provision, SaveProvision, Severity } from "../../services/lensService";
import ConfirmDialog from "./ConfirmDialog";
import { SEVERITIES, formatWhen } from "./lensUtils";
import { UNGROUPED, sortGroups } from "./provisionGroups";

const ABSENCES: Absence[] = ["required", "recommended", "optional"];
const STATUSES: PositionStatus[] = ["draft", "approved"];

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4) },
    title: { display: "flex", alignItems: "baseline", gap: theme.spacing(2), marginBottom: theme.spacing(4) },
    note: { marginBottom: theme.spacing(4) },
    actions: { display: "flex", gap: theme.spacing(2), marginTop: theme.spacing(4) },
}));

interface FormState {
    name: string;
    description: string;
    absence: Absence;
    keywords: string;
    status: PositionStatus;
    absenceSeverity: Severity;
    requirement: string;
    housePosition: string;
    unacceptable: string;
    citation: string;
    modelClause: string;
    // 0 is Ungrouped.
    groupId: number;
}

const toForm = (provision: Provision): FormState => ({
    name: provision.name ?? "",
    description: provision.description ?? "",
    absence: provision.absence,
    keywords: (provision.keywords ?? []).join(", "),
    status: provision.position.status,
    absenceSeverity: provision.position.absenceSeverity,
    requirement: provision.position.requirement ?? "",
    housePosition: provision.position.housePosition ?? "",
    unacceptable: provision.position.unacceptable ?? "",
    citation: provision.position.citation ?? "",
    modelClause: provision.position.modelClause ?? "",
    groupId: provision.groupId ?? 0,
});

interface ProvisionEditorProps {
    provision: Provision;
    groups: PlaybookGroup[];
    readOnly: boolean;
    isSaving: boolean;
    onSave: (provision: SaveProvision) => void;
    onArchive: () => void;
}

const ProvisionEditor = ({ provision, groups, readOnly, isSaving, onSave, onArchive }: ProvisionEditorProps) => {
    const { classes } = useStyles();
    const [form, setForm] = useState<FormState>(() => toForm(provision));
    const [confirmingArchive, setConfirmingArchive] = useState(false);

    useEffect(() => {
        setForm(toForm(provision));
    }, [provision]);

    const set = <K extends keyof FormState>(field: K, value: FormState[K]) => setForm((f) => ({ ...f, [field]: value }));
    const dirty = JSON.stringify(form) !== JSON.stringify(toForm(provision));
    const { approvedByName, approvedOn } = provision.position;
    const approvedLine =
        provision.position.status === "approved" && (approvedByName || approvedOn)
            ? `Approved${approvedByName ? ` by ${approvedByName}` : ""}${approvedOn ? `, ${formatWhen(approvedOn)}` : ""}`
            : undefined;
    // A review note blocks approval until someone marks it resolved.
    const hasNote = !!provision.reviewNote;
    const sortedGroups = sortGroups(groups);

    const save = (reviewNote?: string) =>
        onSave({
            key: provision.key,
            name: form.name.trim(),
            description: form.description.trim(),
            absence: form.absence,
            keywords: form.keywords
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean),
            position: {
                status: form.status !== provision.position.status ? form.status : null,
                requirement: form.requirement.trim(),
                housePosition: form.housePosition.trim(),
                // Empty means no red lines stated, which the API stores as null.
                unacceptable: form.unacceptable.trim() || null,
                absenceSeverity: form.absenceSeverity,
                citation: form.citation.trim(),
                modelClause: form.modelClause.trim(),
            },
            reviewNote,
            // Only sent when changed.
            groupId: form.groupId !== (provision.groupId ?? 0) ? form.groupId : null,
        });

    const text = (field: keyof FormState, label: string, rows = 0) => (
        <TextField
            label={label}
            value={form[field]}
            onChange={(e) => set(field, e.target.value)}
            multiline={rows > 0}
            minRows={rows || undefined}
            disabled={readOnly}
            fullWidth
        />
    );

    return (
        <Paper className={classes.root}>
            <Box className={classes.title}>
                <Typography variant="h6">{provision.name || provision.key}</Typography>
                <Typography variant="caption" style={{ color: "#777" }}>
                    {provision.key}
                </Typography>
            </Box>

            {hasNote && (
                <Alert
                    severity="warning"
                    className={classes.note}
                    action={
                        !readOnly && (
                            <Button color="inherit" size="small" onClick={() => save("")} disabled={isSaving || !form.name.trim()}>
                                Mark resolved
                            </Button>
                        )
                    }
                >
                    {provision.reviewNote}
                </Alert>
            )}

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 8 }}>{text("name", "Name")}</Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        select
                        label="Status"
                        value={form.status}
                        onChange={(e) => set("status", e.target.value as PositionStatus)}
                        helperText={hasNote ? "Resolve the review note before approving." : approvedLine}
                        disabled={readOnly}
                        fullWidth
                    >
                        {STATUSES.map((status) => (
                            <MenuItem key={status} value={status} disabled={hasNote && status === "approved"}>
                                {status}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>{text("description", "Description")}</Grid>
                {sortedGroups.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            select
                            label="Group"
                            value={sortedGroups.some((g) => g.id === form.groupId) ? form.groupId : 0}
                            onChange={(e) => set("groupId", Number(e.target.value))}
                            helperText="Only where it is listed; reviews are unaffected."
                            disabled={readOnly}
                            fullWidth
                        >
                            {sortedGroups.map((g) => (
                                <MenuItem key={g.id} value={g.id}>
                                    {g.name}
                                </MenuItem>
                            ))}
                            <MenuItem value={0}>{UNGROUPED}</MenuItem>
                        </TextField>
                    </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        label="If absent from a document"
                        value={form.absence}
                        onChange={(e) => set("absence", e.target.value as Absence)}
                        disabled={readOnly}
                        fullWidth
                    >
                        {ABSENCES.map((absence) => (
                            <MenuItem key={absence} value={absence}>
                                {absence}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        select
                        label="Severity when absent or defective"
                        value={form.absenceSeverity}
                        onChange={(e) => set("absenceSeverity", e.target.value as Severity)}
                        disabled={readOnly}
                        fullWidth
                    >
                        {SEVERITIES.map((severity) => (
                            <MenuItem key={severity} value={severity}>
                                {severity}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>{text("keywords", "Keywords (comma separated)")}</Grid>
                <Grid size={{ xs: 12 }}>{text("requirement", "Requirement", 3)}</Grid>
                <Grid size={{ xs: 12 }}>{text("housePosition", "House position", 3)}</Grid>
                <Grid size={{ xs: 12 }}>{text("unacceptable", "Unacceptable positions", 2)}</Grid>
                <Grid size={{ xs: 12 }}>{text("citation", "Citation")}</Grid>
                <Grid size={{ xs: 12 }}>{text("modelClause", "Model clause", 4)}</Grid>
            </Grid>

            {!readOnly && (
                <Box className={classes.actions}>
                    <Button
                        variant="contained"
                        onClick={() => save()}
                        disabled={isSaving || !dirty || !form.name.trim() || (hasNote && form.status === "approved")}
                    >
                        Save provision
                    </Button>
                    <Button color="error" onClick={() => setConfirmingArchive(true)} disabled={isSaving}>
                        Archive
                    </Button>
                </Box>
            )}

            <ConfirmDialog
                open={confirmingArchive}
                title={`Archive "${provision.name || provision.key}"?`}
                confirmLabel="Archive"
                destructive
                onClose={() => setConfirmingArchive(false)}
                onConfirm={onArchive}
            >
                It is taken out of this playbook and will not be used in future reviews. It can be restored later.
            </ConfirmDialog>
        </Paper>
    );
};

export default ProvisionEditor;
